import { useCallback, useMemo } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useDocumentContextOptional } from '@/features/printables/contexts/DocumentContext';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { getColorPalette } from '../lib/paletteLookup';
import { getVibe, vibeSupportsDisplaySetting } from '../lib/vibeLookup';
import type {
  DisplaySetting,
  Modifiers,
  StyledRegion,
  StyleProps,
} from '../vibe.types';

// Resolves the active vibe's styles and typed capabilities without exposing its
// id. Components stay vibe-agnostic: visual differences live in style slots and
// supported behavior is queried by setting name.
//
// Every step is memoized because this runs per *event* — a list can call it hundreds of
// times in one render, and unmemoized each call spread the experience, searched the
// palette list and allocated two closures. The context providers memoize the experience
// they hand down, so these deps hold in the common case.
type Ret = {
  supportsDisplaySetting: (setting: DisplaySetting) => boolean;
  vibe: (region: StyledRegion, modifiers?: Partial<Modifiers>) => StyleProps;
};

export const useVibe = (): Ret => {
  const isMobile = useIsMobileBreakpoint();
  const experienceContext = usePageExperienceContext();
  // Vibe id, palette and color are planner-wide, so they resolve parent-first.
  // The Display-section toggles are per-view, so overlay them from the active
  // view (child) experience — the vibe slots read these straight off `experience`.
  const base =
    experienceContext.parentExperience ?? experienceContext.experience;
  const view = experienceContext.experience;
  // Optional, not required: the vibe styles the interactive layouts too, and those
  // render in the plain app tree with no printable document around them. A card on a
  // legacy Page experience is the case that matters — it reaches a vibe slot through
  // EventCardContainer with no DocumentContextProvider anywhere above it, and the
  // throwing hook took the whole page down. Absent means "not printing".
  const isPrintEnv = useDocumentContextOptional()?.isPrintEnv ?? false;

  const experience = useMemo(
    () => ({
      ...base,
      datesInPrimaryColor: view.datesInPrimaryColor,
      shadeWeekendsEnabled: view.shadeWeekendsEnabled,
      muteWeekendDatesEnabled: view.muteWeekendDatesEnabled,
      // Properties of the view, never of the planner around it: a slot asking what
      // it renders into needs the child's. The parent draws nothing.
      layoutId: view.layoutId,
      kind: view.kind,
    }),
    [
      base,
      view.datesInPrimaryColor,
      view.shadeWeekendsEnabled,
      view.muteWeekendDatesEnabled,
      view.layoutId,
      view.kind,
    ]
  );

  // Both resolve to module-level objects (COLOR_PALETTES / VIBES entries), so the
  // memo keys off the persisted string and the identity stays stable.
  const colorPalette = useMemo(
    () => getColorPalette(experience.colorPalette),
    [experience.colorPalette]
  );
  const activeVibe = useMemo(() => getVibe(experience.vibe), [experience.vibe]);

  const resolver = useCallback<Ret['vibe']>(
    (region, modifiers = {}) => {
      const styleFunction = activeVibe.style[region];
      if (!styleFunction) {
        return {};
      }
      return styleFunction({
        ...modifiers,
        experience,
        palette: colorPalette,
        isPrintEnv,
        isMobile,
      });
    },
    [activeVibe, experience, colorPalette, isPrintEnv, isMobile]
  );

  const supportsDisplaySetting = useCallback(
    (setting: DisplaySetting) =>
      vibeSupportsDisplaySetting(experience.vibe, setting),
    [experience.vibe]
  );

  // So a consumer can put the whole return value in its own dep array without
  // defeating the memoization above.
  return useMemo(
    () => ({ supportsDisplaySetting, vibe: resolver }),
    [supportsDisplaySetting, resolver]
  );
};
